import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/CompanyManagement.css';
import { authStorage } from '../services/authStorage';
import { authApi, ApiError } from '../services/authApi';
import { Company } from '../types/auth';
import { getAvatarUrl } from '../utils/avatarUtils';
import { useDataRefresh } from '../contexts/DataRefreshContext';
import { useToast } from '../contexts/ToastContext';
import { formatDate } from '../utils/dateUtils';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface CompanyUser {
  id: string;
  name: string;
  email: string;
  role: string;
  status: 'active' | 'inactive';
  lastLogin?: string;
}

interface NewCompany {
  companyName: string;
  orgNumber: string;
}

interface EditCompany {
  id: number;
  companyName: string;
  orgNumber: string;
  isActive: boolean;
}

interface NewUser {
  username: string;
  email: string;
  personalNumber: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface CreateCompanyRequest {
  companyName: string;
  orgNumber: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface CreateUserRequest {
  profileImageUrl: string | null;
  username: string;
  personalNumber: string | null;
  email: string;
  userType: number;
  companyId: number | null;
}

type SortField = 'companyName' | 'orgNumber' | 'isActive' | 'lastUpdatedDate' | 'userCount';
type SortDirection = 'asc' | 'desc';

interface CompanyManagementProps {
  onCompanyClick?: (company: Company) => void;
  onViewTestSuite?: (company: Company) => void;
}

const CompanyManagement: React.FC<CompanyManagementProps> = ({ onCompanyClick, onViewTestSuite }) => {
  const { t } = useTranslation();
  const { refreshCompanies, refreshAllData } = useDataRefresh();
  const { showSuccess, showError } = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [editCompany, setEditCompany] = useState<EditCompany | null>(null);
  const [sortField, setSortField] = useState<SortField>('companyName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [newCompany, setNewCompany] = useState<NewCompany>({
    companyName: '',
    orgNumber: ''
  });
  const [newUser, setNewUser] = useState<NewUser>({
    username: '',
    email: '',
    personalNumber: ''
  });
  const companiesPerPage = 10;

  // Check if current user is admin
  const isAdmin = () => {
    const userData = authStorage.getUserData();
    return userData?.companyId === null && userData?.userType === 1;
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }
      const response = await authApi.getAllCompanies(token);
      setCompanies(response);

      // Reset to first page when data changes
      setCurrentPage(1);
    } catch (error) {
      console.error('Failed to load companies:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to load companies. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter companies based on search term
  const getFilteredCompanies = () => {
    let filtered = companies;

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(company =>
        company.companyName.toLowerCase().includes(search) ||
        company.orgNumber.toLowerCase().includes(search)
      );
    }

    return filtered;
  };

  const filteredCompanies = getFilteredCompanies();
  const totalPages = Math.ceil(filteredCompanies.length / companiesPerPage);

  // Ensure current page is valid for filtered results
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const sortCompanies = (companies: Company[]) => {
    return [...companies].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Handle user count sorting
      if (sortField === 'userCount') {
        aValue = a.users?.length || 0;
        bValue = b.users?.length || 0;
      }
      // Handle date sorting
      else if (sortField === 'lastUpdatedDate') {
        aValue = new Date(a.lastUpdatedDate).getTime();
        bValue = new Date(b.lastUpdatedDate).getTime();
      }
      // Handle other fields
      else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getCurrentPageCompanies = () => {
    const sortedCompanies = sortCompanies(filteredCompanies);
    const startIndex = (currentPage - 1) * companiesPerPage;
    return sortedCompanies.slice(startIndex, startIndex + companiesPerPage);
  };

  const handleViewUsers = (company: Company) => {
    setSelectedCompany(company);
    setShowUsersModal(true);
  };

  const handleCloseUsersModal = () => {
    setShowUsersModal(false);
    setSelectedCompany(null);
  };

  const handleAddCompany = () => {
    setShowAddCompanyModal(true);
  };

  const handleCloseAddCompanyModal = () => {
    setShowAddCompanyModal(false);
    setNewCompany({
      companyName: '',
      orgNumber: ''
    });
  };

  const handleSaveCompany = async () => {
    try {
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }
      await authApi.createCompany(token, newCompany);
      await loadCompanies(); // Reload companies
      handleCloseAddCompanyModal();

      // Refresh AdminViewPage data if context is available
      if (refreshCompanies) {
        await refreshCompanies();
      }
    } catch (error) {
      console.error('Failed to create company:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to create company. Please try again.');
      }
    }
  };

  const handleInputChange = (field: keyof NewCompany, value: string) => {
    setNewCompany(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Company edit and delete handlers
  const handleEditCompany = (company: Company) => {
    setEditCompany({
      id: company.id,
      companyName: company.companyName,
      orgNumber: company.orgNumber,
      isActive: company.isActive
    });
    setShowEditCompanyModal(true);
  };

  const handleCloseEditCompanyModal = () => {
    setShowEditCompanyModal(false);
    setEditCompany(null);
  };

  const handleSaveEditCompany = async () => {
    if (!editCompany || !isAdmin()) {
      showError(t('notifications.error.unauthorized'));
      return;
    }

    try {
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        showError(t('notifications.error.unauthorized'));
        return;
      }

      await authApi.updateCompany(token, editCompany.id, {
        companyName: editCompany.companyName,
        orgNumber: editCompany.orgNumber,
        isActive: editCompany.isActive
      });

      await loadCompanies(); // Reload companies
      handleCloseEditCompanyModal();

      // Show success notification
      showSuccess(t('notifications.success.companyUpdated'));

      // Refresh AdminViewPage data if context is available
      if (refreshCompanies) {
        await refreshCompanies();
      }
    } catch (error) {
      console.error('Failed to update company:', error);
      if (error instanceof ApiError) {
        showError(error.message);
      } else {
        showError(t('notifications.error.companyUpdateFailed'));
      }
    }
  };

  const handleDeleteCompany = async (companyId: number) => {
    if (!isAdmin()) {
      showError(t('notifications.error.unauthorized'));
      return;
    }

    if (!window.confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
      return;
    }

    try {
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        showError(t('notifications.error.unauthorized'));
        return;
      }

      const result = await authApi.deleteCompany(token, companyId);

      // Immediately remove the company from the local state
      setCompanies(prevCompanies => prevCompanies.filter(company => company.id !== companyId));

      // Show success notification with backend message
      showSuccess(result.message || t('notifications.success.companyDeleted'));

      // Refresh AdminViewPage data if context is available
      if (refreshCompanies) {
        await refreshCompanies();
      }
    } catch (error) {
      console.error('Failed to delete company:', error);
      if (error instanceof ApiError) {
        showError(error.message);
      } else {
        showError(t('notifications.error.companyDeleteFailed'));
      }
    }
  };

  const handleEditInputChange = (field: keyof EditCompany, value: string | boolean) => {
    if (!editCompany) return;
    setEditCompany(prev => prev ? ({ ...prev, [field]: value }) : null);
  };

  // User management functions
  const handleAddUser = (company: Company) => {
    setSelectedCompany(company);
    setShowAddUserModal(true);
  };

  const handleCloseAddUserModal = () => {
    setShowAddUserModal(false);
    setNewUser({
      username: '',
      email: '',
      personalNumber: ''
    });
  };

  const handleSaveUser = async () => {
    if (!selectedCompany) return;

    try {
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      // Create user with default password (username)
      await authApi.createUserWithPassword(token, {
        profileImageUrl: null, // Will use dicebear avatar based on username
        username: newUser.username,
        personalNumber: newUser.personalNumber || null,
        email: newUser.email,
        userType: 2, // Company user
        companyId: selectedCompany.id
      }, newUser.username);

      // Reload companies to show updated user list
      await loadCompanies();
      handleCloseAddUserModal();

      // If users modal is still open, refresh selected company data
      if (showUsersModal && selectedCompany) {
        const updatedCompanies = await authApi.getAllCompanies(token);
        const updatedCompany = updatedCompanies.find(c => c.id === selectedCompany.id);
        if (updatedCompany) {
          setSelectedCompany(updatedCompany);
        }
      }

      // Refresh AdminViewPage data if context is available
      if (refreshAllData) {
        await refreshAllData();
      }
    } catch (error) {
      console.error('Failed to create user:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to create user. Please try again.');
      }
    }
  };

  const handleUserInputChange = (field: keyof NewUser, value: string) => {
    setNewUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="company-management">
        <div className="loading">
          {t('common.loading', 'Loading companies...')}
        </div>
      </div>
    );
  }

  return (
    <div className="company-management">
      <div className="company-management-header">
        <h1>{t('companyManagement.title', 'Company Management')}</h1>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Search Controls Row */}
      <div className="controls-row">
        <div className="controls-left">
          <div className="search-section">
            <div className="search-bar">
              <input
                type="text"
                placeholder={String(t('common.searchCompanies', 'Search companies by name or organization number...'))}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="search-input"
                disabled={loading}
              />
              {searchTerm && (
                <button
                  onClick={clearFilters}
                  className="clear-filters-btn"
                  title={String(t('common.clearFilters', 'Clear filters'))}
                  disabled={loading}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="add-company-section">
          <button className="add-company-button" onClick={handleAddCompany}>
            {t('companyManagement.addCompany', 'Add Company')}
          </button>
        </div>
      </div>

      {/* Results summary */}
      <div className="results-summary">
        {searchTerm ? (
          <p>{t('common.showingResults', 'Showing {{count}} of {{total}} companies', {
            count: filteredCompanies.length,
            total: companies.length
          })}</p>
        ) : (
          <p>{t('common.totalCompanies', 'Total companies: {{count}}', { count: companies.length })}</p>
        )}
      </div>

      <div className="companies-table-container">
        <table className="companies-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort('companyName')}>
                {t('common.companyName', 'Company Name')} <span className="sort-icon">↑</span>
              </th>
              <th className="sortable" onClick={() => handleSort('orgNumber')}>
                {t('common.orgNumber', 'Organization Number')} <span className="sort-icon">↑</span>
              </th>
              <th className="sortable" onClick={() => handleSort('userCount')}>
                {t('common.users', 'Users')} <span className="sort-icon">↑</span>
              </th>
              <th className="sortable" onClick={() => handleSort('isActive')}>
                {t('common.status', 'Status')} <span className="sort-icon">↑</span>
              </th>
              <th className="sortable" onClick={() => handleSort('lastUpdatedDate')}>
                {t('common.updated', 'Last Updated')} <span className="sort-icon">↑</span>
              </th>
              <th className="actions-column">
                {t('common.actions', 'Actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {getCurrentPageCompanies().map((company) => (
              <tr
                key={company.id}
                className={`company-row ${onCompanyClick ? 'clickable' : ''}`}
                onClick={() => {
                  console.log('[CompanyManagement] Company row clicked:', company.companyName, company.id);
                  if (onCompanyClick) {
                    console.log('[CompanyManagement] Calling onCompanyClick handler');
                    onCompanyClick(company);
                  } else {
                    console.warn('[CompanyManagement] No onCompanyClick handler provided');
                  }
                }}
                style={{ cursor: onCompanyClick ? 'pointer' : 'default' }}
              >
                <td className="company-cell">
                  <div className="company-info-cell">
                    <img
                      src={`https://api.dicebear.com/7.x/shapes/svg?seed=${encodeURIComponent(company.companyName)}`}
                      alt={company.companyName}
                      className="company-avatar-small"
                    />
                    <span className="company-name">{company.companyName}</span>
                  </div>
                </td>
                <td className="company-cell">{company.orgNumber}</td>
                <td className="company-cell">
                  <span className="user-count-badge">
                    {company.users?.length || 0} {t('common.users', 'users')}
                  </span>
                </td>
                <td className="company-cell">
                  <span className={`status-badge ${company.isActive ? 'active' : 'inactive'}`}>
                    {company.isActive ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                  </span>
                </td>
                <td className="company-cell">
                  {formatDate(company.lastUpdatedDate)}
                </td>
                <td className="company-cell">
                  <div className="company-actions">
                    <button
                      className="action-button view"
                      onClick={(e) => { e.stopPropagation(); handleViewUsers(company); }}
                      title={String(t('common.viewUsers', 'View Users'))}
                    >
                      👥
                    </button>
                    {onViewTestSuite && (
                      <button
                        className="action-button testsuite"
                        onClick={(e) => { e.stopPropagation(); onViewTestSuite(company); }}
                        title="View Test Suite"
                      >
                        📋
                      </button>
                    )}
                    {isAdmin() && (
                      <>
                        <button
                          className="action-button edit"
                          onClick={(e) => { e.stopPropagation(); handleEditCompany(company); }}
                          title={String(t('common.edit', 'Edit'))}
                        >
                          ✏️
                        </button>
                        <button
                          className="action-button delete"
                          onClick={(e) => { e.stopPropagation(); handleDeleteCompany(company.id); }}
                          title={String(t('common.delete', 'Delete'))}
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination - only show if there are companies and multiple pages */}
      {filteredCompanies.length > 0 && totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-button"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            «
          </button>
          <button
            className="pagination-button"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            ‹
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={`pagination-button ${currentPage === page ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}

          <button
            className="pagination-button"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            ›
          </button>
          <button
            className="pagination-button"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            »
          </button>
        </div>
      )}

      {/* No results message */}
      {filteredCompanies.length === 0 && !loading && (
        <div className="no-results">
          <p>{searchTerm ?
            t('common.noResultsFound', 'No companies found matching your search criteria.') :
            t('common.noCompaniesFound', 'No companies found.')
          }</p>
          {searchTerm && (
            <button onClick={clearFilters} className="btn-secondary">
              {t('common.clearFilters', 'Clear filters')}
            </button>
          )}
        </div>
      )}

      {/* Users Modal */}
      {showUsersModal && selectedCompany && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{selectedCompany.companyName} - {t('common.users', 'Users')}</h2>
              <div className="modal-header-actions">
                <button
                  className="btn-primary add-user-btn"
                  onClick={() => handleAddUser(selectedCompany)}
                  title={String(t('common.addUser', 'Add User'))}
                >
                  {t('common.addUser', 'Add User')}
                </button>
              </div>
            </div>
            <div className="modal-body">
              {selectedCompany.users && selectedCompany.users.length > 0 ? (
                <div className="company-users-list">
                  {selectedCompany.users.map((user) => (
                    <div key={user.id} className="company-user-item">

                      <img
                        className="user-avatar-small"
                        src={getAvatarUrl(user.profileImageUrl, user.username)}
                        alt={user.username}
                      />

                      <div className="user-info">
                        <h4>{user.username}</h4>
                        <p>{user.email}</p>
                        {user.personalNumber && (
                          <p className="user-personal-number">ID: {user.personalNumber}</p>
                        )}
                        <span className={`user-status ${user.isActive ? 'active' : 'inactive'}`}>
                          {user.isActive ? 'Active' : 'Inactive'} · {user.isVerified ? 'Verified' : 'Not Verified'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-users">
                  <p>{t('common.noUsers', 'No users found for this company.')}</p>
                  <button
                    className="btn-primary"
                    onClick={() => handleAddUser(selectedCompany)}
                  >
                    {t('common.addFirstUser', 'Add First User')}
                  </button>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseUsersModal}>
                {t('common.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Company Modal */}
      {showAddCompanyModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{t('companyManagement.addNewCompany', 'Add New Company')}</h2>
              <button className="modal-close" onClick={handleCloseAddCompanyModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="add-company-form">
                <div className="form-group">
                  <label htmlFor="companyName">{t('common.companyName', 'Company Name')}</label>
                  <input
                    type="text"
                    id="companyName"
                    value={newCompany.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    placeholder={String(t('common.enterCompanyName', 'Enter company name'))}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="orgNumber">{t('common.orgNumber', 'Organization Number')}</label>
                  <input
                    type="text"
                    id="orgNumber"
                    value={newCompany.orgNumber}
                    onChange={(e) => handleInputChange('orgNumber', e.target.value)}
                    placeholder={String(t('common.enterOrgNumber', 'Enter organization number'))}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseAddCompanyModal}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveCompany}
                disabled={!newCompany.companyName.trim() || !newCompany.orgNumber.trim()}
              >
                {t('common.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Company Modal */}
      {showEditCompanyModal && editCompany && isAdmin() && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{t('companyManagement.editCompany', 'Edit Company')}</h2>
              <button className="modal-close" onClick={handleCloseEditCompanyModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="add-company-form">
                <div className="form-group">
                  <label htmlFor="editCompanyName">{t('common.companyName', 'Company Name')}</label>
                  <input
                    type="text"
                    id="editCompanyName"
                    value={editCompany.companyName}
                    onChange={(e) => handleEditInputChange('companyName', e.target.value)}
                    placeholder={String(t('common.enterCompanyName', 'Enter company name'))}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="editOrgNumber">{t('common.orgNumber', 'Organization Number')}</label>
                  <input
                    type="text"
                    id="editOrgNumber"
                    value={editCompany.orgNumber}
                    onChange={(e) => handleEditInputChange('orgNumber', e.target.value)}
                    placeholder={String(t('common.enterOrgNumber', 'Enter organization number'))}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="editIsActive">{t('common.status', 'Status')}</label>
                  <select
                    id="editIsActive"
                    value={editCompany.isActive ? 'true' : 'false'}
                    onChange={(e) => handleEditInputChange('isActive', e.target.value === 'true')}
                  >
                    <option value="true">{t('common.active', 'Active')}</option>
                    <option value="false">{t('common.inactive', 'Inactive')}</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseEditCompanyModal}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveEditCompany}
                disabled={!editCompany.companyName.trim() || !editCompany.orgNumber.trim()}
              >
                {t('common.save', 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && selectedCompany && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{t('common.addUserFor', 'Add User for')} {selectedCompany.companyName}</h2>
              <button className="modal-close" onClick={handleCloseAddUserModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="add-user-form">
                <div className="form-group">
                  <label htmlFor="userName">{t('common.username', 'Username')}</label>
                  <input
                    type="text"
                    id="userName"
                    value={newUser.username}
                    onChange={(e) => handleUserInputChange('username', e.target.value)}
                    placeholder={String(t('common.enterUsername', 'Enter username'))}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="userEmail">{t('common.email', 'Email')}</label>
                  <input
                    type="email"
                    id="userEmail"
                    value={newUser.email}
                    onChange={(e) => handleUserInputChange('email', e.target.value)}
                    placeholder={String(t('common.enterEmail', 'Enter email address'))}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="userPersonalNumber">{t('common.personalNumber', 'Personal Number')}</label>
                  <input
                    type="text"
                    id="userPersonalNumber"
                    value={newUser.personalNumber}
                    onChange={(e) => handleUserInputChange('personalNumber', e.target.value)}
                    placeholder={String(t('common.enterPersonalNumber', 'Enter personal number (optional)'))}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseAddUserModal}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveUser}
                disabled={!newUser.username.trim() || !newUser.email.trim()}
              >
                {t('common.save', 'Save User')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyManagement;
